import type { ColumnSpec, DefinitionKey, DefinitionProblem, DefinitionValidation, DefinitionValues, ParameterSchema, ParameterSpec, ParameterType } from './types'
import { DEFINITION_EXCERPT_MAX_CHARS, DEFINITION_NOTES_MAX_CHARS, DEFINITION_TEXT_MAX_CHARS, PARAMETER_KEY_RE, ROW_KEY_RE } from './types'

/**
 * PRD-026 §3.14 validation at publish, pure: unknown key, required key empty, type mismatch, duplicate row key.
 * The editor cannot produce an unknown key, so `definition.key_unknown` is a guard against a hand-made request,
 * not a form error. A `notes` value is checked for length only and never enters an agent's bundle (§3.18).
 *
 * Prior art: Terraform variable validation (type + `validation` block at plan time; an undeclared variable is refused).
 */

const DECIMAL_RE = /^-?\d+\.\d{2}$/
const INTEGER_RE = /^-?\d{1,12}$/
const PERCENT_RE = /^\d{1,3}\.\d{2}$/
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/
const ACCOUNT_TOKEN_RE = /^acct:[\w.-]{1,64}$/
const PARTY_RE = /^[^\n\r]{1,200}$/

/** "Not set": undefined, null, a blank string or an empty list. Shared with the effective-skill composer (ADR 0052), which treats an empty key at one scope as "look wider". */
export function isEmptyValue(v: unknown): boolean {
  return v === undefined || v === null || (typeof v === 'string' && v.trim() === '') || (Array.isArray(v) && v.length === 0)
}

const isEmpty = isEmptyValue

/** True when `v` is a well-formed value of `type`. Empty values are handled by the caller (required or not). */
export function valueMatchesType(type: Exclude<ParameterType, 'table'>, v: unknown, allowed?: ReadonlyArray<string>): boolean {
  switch (type) {
    case 'account_token':
      return typeof v === 'string' && ACCOUNT_TOKEN_RE.test(v)
    case 'text':
      return typeof v === 'string' && !/[\n\r]/.test(v) && v.length <= DEFINITION_TEXT_MAX_CHARS
    case 'notes':
      return typeof v === 'string' && v.length <= DEFINITION_NOTES_MAX_CHARS
    case 'decimal':
      return typeof v === 'string' && DECIMAL_RE.test(v)
    case 'integer':
      return (typeof v === 'number' && Number.isInteger(v) && Math.abs(v) < 1e12) || (typeof v === 'string' && INTEGER_RE.test(v))
    case 'percent':
      return typeof v === 'string' && PERCENT_RE.test(v) && Number(v) >= 0 && Number(v) <= 100
    case 'date':
      return typeof v === 'string' && DATE_RE.test(v) && !Number.isNaN(new Date(`${v}T00:00:00Z`).getTime())
    case 'boolean':
      return typeof v === 'boolean'
    case 'enum':
      return typeof v === 'string' && (allowed ?? []).includes(v)
    case 'party':
      return typeof v === 'string' && PARTY_RE.test(v)
  }
}

/** The Logic indexer's slugify, reused for table row keys so `row-<key>` and `<param>.<row>` agree. */
export function slugifyRowKey(s: string): string {
  return s.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 64)
}

export function isDefinitionValues(v: unknown): v is DefinitionValues {
  if (typeof v !== 'object' || v === null)
    return false
  const o = v as Record<string, unknown>
  return o.v === 1 && typeof o.values === 'object' && o.values !== null && !Array.isArray(o.values)
}

/** A schema is well-formed when every key and column key matches the grammar and every table names a `row_key` column. Thrown at catalog load, never at runtime. */
export function assertSchemaWellFormed(schema: ParameterSchema): void {
  const seen = new Set<string>()
  for (const p of schema.parameters) {
    if (!PARAMETER_KEY_RE.test(p.key))
      throw new Error(`schema ${schema.slug}: parameter key "${p.key}" is malformed`)
    if (seen.has(p.key))
      throw new Error(`schema ${schema.slug}: duplicate parameter key "${p.key}"`)
    seen.add(p.key)
    if (p.type === 'enum' && (!p.enum || p.enum.length === 0))
      throw new Error(`schema ${schema.slug}: enum "${p.key}" declares no values`)
    if (p.type === 'table') {
      if (!p.columns || p.columns.length === 0 || !p.row_key)
        throw new Error(`schema ${schema.slug}: table "${p.key}" needs columns and a row_key`)
      const cols = new Set<string>()
      for (const c of p.columns) {
        if (!PARAMETER_KEY_RE.test(c.key) || cols.has(c.key))
          throw new Error(`schema ${schema.slug}: table "${p.key}" column "${c.key}" is malformed or duplicated`)
        cols.add(c.key)
      }
      if (!cols.has(p.row_key))
        throw new Error(`schema ${schema.slug}: table "${p.key}" row_key "${p.row_key}" is not a column`)
    }
  }
}

function checkScalar(problems: DefinitionProblem[], path: string, spec: { type: Exclude<ParameterType, 'table'>, required: boolean, enum?: ReadonlyArray<string> | undefined, label: string }, v: unknown): void {
  if (isEmpty(v)) {
    if (spec.required)
      problems.push({ rule: 'definition.key_required', key: path, detail: `${spec.label} is required.` })
    return
  }
  if (!valueMatchesType(spec.type, v, spec.enum))
    problems.push({ rule: 'definition.value_invalid', key: path, detail: `${spec.label} is not a valid ${spec.type}${spec.type === 'enum' ? ` (one of ${(spec.enum ?? []).join(', ')})` : spec.type === 'decimal' ? ' (two decimal places, no separators)' : ''}.` })
}

function checkTable(problems: DefinitionProblem[], p: ParameterSpec, v: unknown): void {
  if (isEmpty(v)) {
    if (p.required)
      problems.push({ rule: 'definition.key_required', key: p.key, detail: `${p.label} needs at least one row.` })
    return
  }
  if (!Array.isArray(v)) {
    problems.push({ rule: 'definition.value_invalid', key: p.key, detail: `${p.label} must be a list of rows.` })
    return
  }
  const columns = p.columns ?? []
  const rowKeyCol = p.row_key!
  const seen = new Set<string>()
  v.forEach((row, i) => {
    if (typeof row !== 'object' || row === null || Array.isArray(row)) {
      problems.push({ rule: 'definition.value_invalid', key: `${p.key}[${i}]`, detail: `Row ${i + 1} of ${p.label} is not an object.` })
      return
    }
    const r = row as Record<string, unknown>
    for (const k of Object.keys(r)) {
      if (!columns.some(c => c.key === k))
        problems.push({ rule: 'definition.key_unknown', key: `${p.key}[${i}].${k}`, detail: `Row ${i + 1} of ${p.label} has a column "${k}" the schema does not declare.` })
    }
    const rawKey = r[rowKeyCol]
    const rowKey = typeof rawKey === 'string' ? slugifyRowKey(rawKey) : ''
    if (!rowKey || !ROW_KEY_RE.test(rowKey)) {
      problems.push({ rule: 'definition.value_invalid', key: `${p.key}[${i}].${rowKeyCol}`, detail: `Row ${i + 1} of ${p.label} needs a ${rowKeyCol} that yields a key (letters, digits, hyphens).` })
    }
    else if (seen.has(rowKey)) {
      problems.push({ rule: 'definition.row_duplicate', key: `${p.key}.${rowKey}`, detail: `${p.label} has two rows keyed "${rowKey}".` })
    }
    else {
      seen.add(rowKey)
    }
    for (const c of columns) {
      if (c.key === rowKeyCol)
        continue
      checkScalar(problems, `${p.key}[${i}].${c.key}`, { type: c.type, required: c.required, enum: c.enum, label: `${p.label} · ${c.label}` }, r[c.key])
    }
  })
}

/** PRD-026 §3.14: the whole publish-time check. `values` is the stored shape `{ v: 1, values: {…} }`. */
export function validateDefinitionValues(schema: ParameterSchema, values: unknown): DefinitionValidation {
  if (!isDefinitionValues(values))
    return { ok: false, problems: [{ rule: 'definition.values_unknown_version', key: '', detail: 'Values must be `{ v: 1, values: {…} }`.' }] }
  const problems: DefinitionProblem[] = []
  const declared = new Set(schema.parameters.map(p => p.key))
  for (const k of Object.keys(values.values)) {
    if (!declared.has(k))
      problems.push({ rule: 'definition.key_unknown', key: k, detail: `"${k}" is not a parameter of ${schema.title}.` })
  }
  for (const p of schema.parameters) {
    const v = values.values[p.key]
    if (p.type === 'table')
      checkTable(problems, p, v)
    else
      checkScalar(problems, p.key, { type: p.type, required: p.required, enum: p.enum, label: p.label }, v)
  }
  return { ok: problems.length === 0, problems }
}

function renderScalar(type: Exclude<ParameterType, 'table'>, v: unknown): string {
  if (isEmpty(v))
    return '—'
  if (type === 'boolean')
    return v ? 'yes' : 'no'
  return String(v).replace(/\s+/g, ' ').trim()
}

function clip(s: string): string {
  return s.length <= DEFINITION_EXCERPT_MAX_CHARS ? s : `${s.slice(0, DEFINITION_EXCERPT_MAX_CHARS - 1)}…`
}

/**
 * PRD-026 §6.6 the key index: one `value` row per set scalar parameter, one `row` row per table row keyed
 * `<param>.<row>`. Empty optional parameters are not indexed (there is nothing to cite). Assumes `validateDefinitionValues` passed.
 */
export function buildKeyIndex(schema: ParameterSchema, values: DefinitionValues): DefinitionKey[] {
  const out: DefinitionKey[] = []
  let position = 0
  for (const p of schema.parameters) {
    const v = values.values[p.key]
    if (isEmpty(v))
      continue
    if (p.type === 'table') {
      const cols = p.columns ?? []
      for (const row of v as ReadonlyArray<Record<string, unknown>>) {
        const rowKey = slugifyRowKey(String(row[p.row_key!] ?? ''))
        const cells = cols.filter(c => c.key !== p.row_key).map(c => renderScalar(c.type, row[c.key]))
        out.push({ key: `${p.key}.${rowKey}`, kind: 'row', label: String(row[p.row_key!] ?? rowKey), excerpt: clip(cells.join(' · ') || rowKey), position: ++position })
      }
      continue
    }
    out.push({ key: p.key, kind: 'value', label: p.label, excerpt: clip(renderScalar(p.type, v)), position: ++position })
  }
  return out
}

/** The keys a later version dropped (the editor's warning, as for Logic rules). */
export function droppedKeys(previous: ReadonlyArray<{ key: string }>, next: ReadonlyArray<{ key: string }>): string[] {
  const keep = new Set(next.map(k => k.key))
  return previous.map(k => k.key).filter(k => !keep.has(k))
}

/** The values a new object starts from: every declared default. */
export function defaultValues(schema: ParameterSchema): DefinitionValues {
  const values: Record<string, unknown> = {}
  for (const p of schema.parameters) {
    if (p.default !== undefined)
      values[p.key] = p.default
  }
  return { v: 1, values }
}

export type { ColumnSpec }
